import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode';
import { useZNSName } from '../../hooks/useZNSName';
import RecentTransactions from './RecentTransactions';

interface RequestTabProps {
  userAddress: `0x${string}` | undefined;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
`;

const QRContainer = styled.div`
  padding: 20px;
  background: #ffffff;
  border-radius: 16px;
  display: inline-block;
`;

const InfoSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 10px;
  gap: 12px;
`;

const InfoLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: 13px;
  color: #ffffff;
  font-family: 'monospace';
  flex: 1;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CopyButton = styled.button`
  padding: 6px 12px;
  background: rgba(168, 85, 247, 0.2);
  border: 1px solid rgba(168, 85, 247, 0.4);
  border-radius: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: rgba(168, 85, 247, 0.3);
    border-color: rgba(168, 85, 247, 0.6);
  }
`;

const ShareButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(124, 58, 237, 0.3));
  border: 1px solid rgba(168, 85, 247, 0.6);
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.5), rgba(124, 58, 237, 0.5));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
  }
`;

const NoZNSText = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  font-style: italic;
`;

const RequestTab: React.FC<RequestTabProps> = ({ userAddress }) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [copied, setCopied] = useState<'address' | 'zns' | null>(null);
  const { znsName } = useZNSName(userAddress);

  useEffect(() => {
    if (userAddress) {
      QRCode.toDataURL(userAddress, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      })
        .then(setQrDataUrl)
        .catch(console.error);
    }
  }, [userAddress]);

  const handleCopy = (text: string, type: 'address' | 'zns') => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleShare = async () => {
    if (!userAddress) return;

    const shareData = {
      title: 'Send me USDG',
      text: `Send USDG to ${znsName || userAddress}`,
      url: `https://mavrk.ink/pay?to=${userAddress}`,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback: copy link
      handleCopy(shareData.url, 'address');
    }
  };

  if (!userAddress) {
    return (
      <Container>
        <div style={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
          Please connect your wallet to request payments
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {qrDataUrl && (
        <QRContainer>
          <img src={qrDataUrl} alt="Payment QR Code" style={{ display: 'block' }} />
        </QRContainer>
      )}

      <InfoSection>
        <InfoRow>
          <InfoLabel>My Address</InfoLabel>
          <InfoValue>{userAddress.slice(0, 10)}...{userAddress.slice(-8)}</InfoValue>
          <CopyButton onClick={() => handleCopy(userAddress, 'address')}>
            {copied === 'address' ? 'Copied!' : 'Copy'}
          </CopyButton>
        </InfoRow>

        <InfoRow>
          <InfoLabel>My .ink Domain</InfoLabel>
          {znsName ? (
            <>
              <InfoValue>{znsName}</InfoValue>
              <CopyButton onClick={() => handleCopy(znsName, 'zns')}>
                {copied === 'zns' ? 'Copied!' : 'Copy'}
              </CopyButton>
            </>
          ) : (
            <NoZNSText>No .ink name set</NoZNSText>
          )}
        </InfoRow>
      </InfoSection>

      <ShareButton onClick={handleShare}>
        Share Payment Request
      </ShareButton>

      <RecentTransactions userAddress={userAddress} type="received" />
    </Container>
  );
};

export default RequestTab;

